import { useEffect, useRef } from 'react';

// Pendant les phases narratives, l'AudioContext peut ne pas encore être
// initialisé (l'utilisateur n'a pas cliqué). On génère alors un volume
// synthétique réaliste qui oscille même sans micro/audio.
// Quand l'analyser réel est disponible, il prend le relais automatiquement.

export const K2000VoiceScanner = ({ isActive, analyserRef, darkMode }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    let animationFrame;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let offset = 0;

    // Volume synthétique : plusieurs couches de sinus déphasés
    // pour un résultat organique, non répétitif visuellement.
    const getSyntheticVolume = (t) => {
      const base  = 28 + Math.sin(t * 0.0008) * 12;   // onde lente de fond
      const mid   = Math.sin(t * 0.003  + 1.2) * 10;  // modulation moyenne
      const fast  = Math.sin(t * 0.012  + 2.4) * 6;   // micro-variation
      const noise = (Math.random() - 0.5) * 8;         // grain aléatoire
      return Math.max(12, base + mid + fast + noise);
    };

    const render = () => {
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      const now      = Date.now();
      const analyser = analyserRef?.current ?? null;

      let volume = 0;

      if (analyser) {
        // Audio réel disponible — prend le dessus sur le synthétique
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(dataArray);
        volume = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
      } else if (isActive) {
        // isActive=true mais AudioContext pas encore init → synthétique
        volume = getSyntheticVolume(now);
      }
      // isActive=false et analyser=null → volume=0 → ligne de veille

      if (volume > 8) {
        ctx.beginPath();
        ctx.lineWidth   = 2;
        ctx.strokeStyle = darkMode ? '#f59e0b' : '#d97706';
        ctx.shadowBlur  = 14;
        ctx.shadowColor = ctx.strokeStyle;
        ctx.lineCap     = 'round';

        const segments = 80;
        const step     = w / segments;
        ctx.moveTo(0, h / 2);

        for (let i = 0; i <= segments; i++) {
          const x             = i * step;
          const baseDistortion = Math.sin(i * 0.5 + offset) * Math.cos(i * 0.3 - offset * 0.6);
          const voiceJitter    = Math.sin(i * 2.5 + offset * 3) * 0.25;
          const distortion     = baseDistortion + voiceJitter;
          const envelope       = Math.sin((i / segments) * Math.PI);
          const y              = h / 2 + distortion * (volume / 4) * envelope;
          ctx.lineTo(x, y);
        }

        ctx.stroke();
        ctx.shadowBlur = 0;
        offset += 0.15;

      } else {
        // Ligne de veille + curseur mobile discret
        ctx.beginPath();
        ctx.moveTo(0, h / 2);
        ctx.lineTo(w, h / 2);
        ctx.strokeStyle = darkMode ? 'rgba(245,158,11,0.18)' : 'rgba(217,119,6,0.18)';
        ctx.lineWidth   = 1;
        ctx.shadowBlur  = 0;
        ctx.stroke();

        const cx = (offset * 40) % w;
        ctx.beginPath();
        ctx.arc(cx, h / 2, 1.5, 0, Math.PI * 2);
        ctx.fillStyle = darkMode ? 'rgba(245,158,11,0.4)' : 'rgba(217,119,6,0.4)';
        ctx.fill();

        offset += 0.04;
      }

      animationFrame = requestAnimationFrame(render);
    };

    render();
    return () => cancelAnimationFrame(animationFrame);

  // analyserRef intentionnellement absent des deps : c'est une ref stable,
  // son .current est relu à chaque frame. isActive et darkMode déclenchent
  // un restart du loop quand ils changent.
  }, [isActive, darkMode]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <canvas
      ref={canvasRef}
      width={500}
      height={60}
      className="w-full h-full"
    />
  );
};